import { Header } from '@tanstack/react-table';
import * as React from 'react';
import { ElementType } from 'react';

import styles from '../Table.module.scss';
import { useTableContext } from '../TableContext';
import { filter as setFilter } from '../TableService';

import { DataListFilter } from './Filters/DataListFilter';
import { FilterOnChangeFn, FilterPayload } from './Filters/types';

type TableFilterProps<T> = {
  header: Header<T, unknown>;
  name?: string;
};

export type ColumnMetaDef = {
  filterComponent: ElementType;
  filterData?: unknown;
  filterProps?: Record<string, unknown>;
  contentAlignment?: 'left' | 'right' | 'center';
};

function TableFilter<TData>({ header, name }: TableFilterProps<TData>) {
  const { id, column } = header;
  const { columnFilterState } = useTableContext();
  const meta = column.columnDef.meta as ColumnMetaDef;

  const {
    filterComponent: FilterComponent,
    filterData,
    filterProps,
    contentAlignment,
  } = meta || {};

  const value = filterProps?.value || undefined;
  const isManaged = filterProps?.isManaged || undefined;
  const onChange = (filterProps?.onChange as FilterOnChangeFn<TData>) || undefined;

  const handleOnChange = (newValue: unknown, filter?: FilterPayload<TData>) => {
    if (!isManaged) {
      setFilter(id, newValue);
    }
    onChange?.(newValue, filter);
  };

  const columnValue = isManaged ? value : columnFilterState?.get(id);

  return (
    <td key={id} className={styles.filterCol}>
      {header.column.getCanFilter() && (
        <div>
          {FilterComponent && (
            <FilterComponent
              column={column}
              filterData={filterData}
              name={name}
              {...filterProps}
              onChange={handleOnChange}
              value={columnValue}
              contentAlignment={contentAlignment}
            />
          )}
          {!FilterComponent && (
            <DataListFilter
              column={column}
              filterData={filterData as string[]}
              name={name}
              {...filterProps}
              onChange={handleOnChange}
              value={columnValue}
            />
          )}
        </div>
      )}
    </td>
  );
}

export { TableFilter };
